/**
 * Reusable line-style editor popup. Currently driven by the
 * Chart Properties dialog for the live-price line; same shape
 * will host SL / TP line editing once those gain user-tweakable
 * width + style (color is already in the parent theme).
 *
 * Editing model mirrors the parent:
 *   - changes apply LIVE via `onChange` (chart updates as the
 *     user fiddles with the controls)
 *   - the dialog snapshots `value` on each open into a rollback
 *     ref; `Cancel` / `×` / backdrop click calls `onChange(snapshot)`
 *     then `onClose`
 *   - `Save` just calls `onClose` (value already applied)
 *
 * The parent (ChartColorsDialog) holds its OWN rollback for the
 * whole theme, so even if the user saves changes here and then
 * cancels the parent, the line still gets reverted along with
 * everything else.
 */
import { useEffect, useRef } from "react";
import { ColorPicker } from "@/components/ColorPicker/ColorPicker";
import { formatColor, parseColor } from "@/utils/colorWithAlpha";
import { svgDasharrayFromName } from "@/components/Chart/priceLineStyles";
import { LINE_STYLE_OPTIONS } from "@/tools/_shared/lineStyle";
import styles from "./LineStyleDialog.module.css";

export type LineStyleName =
  | "solid"
  | "dotted"
  | "dashed"
  | "largeDashed"
  | "sparseDotted";

export interface LineStyleConfig {
  color: string;
  width: number;
  style: LineStyleName;
}

interface Props {
  open: boolean;
  /** Header title — e.g. "Live price line", "Stop-loss line". */
  title: string;
  value: LineStyleConfig;
  /** Fires on every control change; consumer dispatches the live
   *  update so the chart re-renders in real time. */
  onChange: (next: LineStyleConfig) => void;
  /** Called on Save (after live edits are kept) AND on Cancel
   *  (after onChange has been called with the rollback). The
   *  consumer just needs to close the popup. */
  onClose: () => void;
  /** Skip the color picker row — used by Fibonacci where the
   *  line color is per-level and lives in the parent dialog,
   *  while thickness + style are shared and edited here. The
   *  `value.color` is still required so the style-preview
   *  swatches can render at a representative color. */
  hideColor?: boolean;
}

const WIDTH_OPTIONS = [1, 2, 3, 4];

export function LineStyleDialog({
  open,
  title,
  value,
  onChange,
  onClose,
  hideColor = false,
}: Props) {
  // Rollback snapshot per open. Updated only on the closed→open
  // transition so re-renders mid-edit don't reset it.
  const rollbackRef = useRef<LineStyleConfig | null>(null);
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      rollbackRef.current = { ...value };
    }
    prevOpenRef.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const cancel = () => {
    if (rollbackRef.current) onChange(rollbackRef.current);
    onClose();
  };

  return (
    <div className={styles.backdrop} onClick={cancel} role="presentation">
      <div
        className={styles.dialog}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="line-style-title"
      >
        <header className={styles.header}>
          <h2 id="line-style-title" className={styles.title}>
            {title}
          </h2>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={cancel}
            aria-label="Close (discard changes)"
          >
            ×
          </button>
        </header>

        <div className={styles.body}>
          {!hideColor &&
            (() => {
              const { color: hex, opacity } = parseColor(value.color);
              return (
                <div className={styles.row}>
                  <span className={styles.rowLabel}>Color</span>
                  <ColorPicker
                    value={hex}
                    opacity={opacity}
                    onChange={(c, o) =>
                      onChange({ ...value, color: formatColor(c, o) })
                    }
                    ariaLabel={`${title} color`}
                  />
                </div>
              );
            })()}

          <div className={styles.row}>
            <label className={styles.rowLabel} htmlFor="line-style-width">
              Thickness
            </label>
            <select
              id="line-style-width"
              className={styles.select}
              value={value.width}
              onChange={(e) =>
                onChange({ ...value, width: Number(e.target.value) })
              }
            >
              {WIDTH_OPTIONS.map((w) => (
                <option key={w} value={w}>
                  {w} px
                </option>
              ))}
            </select>
          </div>

          <div className={styles.styleRow}>
            <span className={styles.rowLabel}>Style</span>
            <div className={styles.styleOptions} role="radiogroup">
              {LINE_STYLE_OPTIONS.map((opt) => {
                const active = value.style === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    className={`${styles.styleOption} ${active ? styles.styleOptionActive : ""}`}
                    onClick={() =>
                      onChange({ ...value, style: opt.value })
                    }
                  >
                    {/* SVG preview at the user-picked color + width
                        so the dropdown choice matches the rendered
                        line at a glance. */}
                    <svg
                      className={styles.styleSwatch}
                      viewBox="0 0 80 12"
                      preserveAspectRatio="none"
                      aria-hidden
                    >
                      <line
                        x1="2"
                        y1="6"
                        x2="78"
                        y2="6"
                        stroke={value.color}
                        strokeWidth={value.width}
                        strokeDasharray={svgDasharrayFromName(opt.value)}
                        strokeLinecap="round"
                      />
                    </svg>
                    <span className={styles.styleLabel}>{opt.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <footer className={styles.footer}>
          <button
            type="button"
            className={styles.btnSecondary}
            onClick={cancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={onClose}
          >
            Save
          </button>
        </footer>
      </div>
    </div>
  );
}
